import { POItemGroups, POItemPurchaseOrderGroup, POItemProductGroup, POItemShipmentGroup, POItemSupplierGroup, PurchaseOrderItem, PurchaseOrderItemStatus } from '@domain/purchasing';
import { PurchaseOrderItemDTO } from '../dto/PurchaseOrderDTO';
import type { SupplierProductDTO } from '@infrastructure/product/dto/ProductDTO';
import { ProductMapper } from '../../product/mappers/ProductMapper';
import { ShipmentMapper } from './ShipmentMapper';
import { CreatePurchaseOrderItemDTO } from '../dto/CreatePurchaseOrderDTO';
import { PurchaseOrderMapper } from './PurchaseOrderMapper';
import { POItemsGroupedDTO } from '../dto/POItemsGroupedDto';
import { SupplierMapper } from '@infrastructure/suppliers/mappers/SupplierMapper';

export class PurchaseOrderItemMapper {
  private static optionalNumeric(value: string | number | null | undefined): number | undefined {
    if (value == null || value === '') return undefined;
    const n = typeof value === 'number' ? value : parseFloat(String(value));
    return Number.isFinite(n) ? n : undefined;
  }

  private static nestedSupplierProductDto(dto: PurchaseOrderItemDTO): SupplierProductDTO | null | undefined {
    if (dto.supplierProduct !== undefined) return dto.supplierProduct;
    return dto.linkedProduct;
  }

  static toModel(dto: PurchaseOrderItemDTO): PurchaseOrderItem {
    const nested = PurchaseOrderItemMapper.nestedSupplierProductDto(dto);
    return {
      id: dto.id,
      supplierId: dto.supplierId,
      product: ProductMapper.toModel(dto.product),
      shipment: dto.shipment ? ShipmentMapper.toModel(dto.shipment) : undefined,
      purchaseOrder: dto.purchaseOrder ? PurchaseOrderMapper.toModel(dto.purchaseOrder) : undefined,
      quantity: dto.quantity ?? undefined,
      minimumOrderQuantity: PurchaseOrderItemMapper.optionalNumeric(dto.minimumOrderQuantity ?? undefined),
      orderInMultiplesOf: PurchaseOrderItemMapper.optionalNumeric(dto.orderInMultiplesOf ?? undefined),
      purchasePrice: dto.purchasePrice ? parseFloat(dto.purchasePrice) : undefined,
      currency: dto.currency ?? undefined,
      minimumOrderQuantityType: dto.minimumOrderQuantityType ?? undefined,
      supplierProduct:
        nested === undefined ? undefined : nested === null ? null : SupplierMapper.supplierProductToModel(nested),
      productionStartDate: dto.productionStartDate ? new Date(dto.productionStartDate) : undefined,
      productionReadyDate: dto.productionReadyDate ? new Date(dto.productionReadyDate) : undefined,
      status: dto.status as PurchaseOrderItemStatus,
      statusUpdatedAt: dto.statusUpdatedAt ? new Date(dto.statusUpdatedAt) : undefined,
      sort: dto.sort,
      createdAt: dto.createdAt ? new Date(dto.createdAt) : undefined,
      updatedAt: dto.updatedAt ? new Date(dto.updatedAt) : undefined,
      eta: dto.eta ? new Date(dto.eta) : undefined,
      recommendedQuantity: dto.recommendedQuantity,
    };
  }

  static toCreateDTO(model: PurchaseOrderItem): CreatePurchaseOrderItemDTO {
    return {
      productId: model.product.id,
      supplierId: model.supplierId,
      quantity: model.quantity,
      minimumOrderQuantity: model.minimumOrderQuantity,
      orderInMultiplesOf: model.orderInMultiplesOf,
      minimumOrderQuantityType: model.minimumOrderQuantityType,
      shipment: model.shipment ? ShipmentMapper.updateRequestToDTO(model.shipment) : undefined,
      status: model.status,
      productionStartDate: model.productionStartDate?.toISOString(),
      productionReadyDate: model.productionReadyDate?.toISOString(),
      eta: model.eta?.toISOString(),
    };
  }

  static toGroupedModel<T extends POItemGroups>(dto: POItemsGroupedDTO): T {
    if ('shipment' in dto) {
      return new POItemShipmentGroup(dto.id, dto.name, dto.items.map(item => PurchaseOrderItemMapper.toModel(item)), ShipmentMapper.toModel(dto.shipment)) as T;
    }
    if ('product' in dto) {
      return new POItemProductGroup(dto.id, dto.name, dto.items.map(item => PurchaseOrderItemMapper.toModel(item)), ProductMapper.toModel(dto.product)) as T;
    }
    if ('supplier' in dto) {
      return new POItemSupplierGroup(dto.id, dto.name, dto.items.map(item => PurchaseOrderItemMapper.toModel(item)), SupplierMapper.toModel(dto.supplier)) as T;
    }
    if ('purchaseOrder' in dto) {
      return new POItemPurchaseOrderGroup(dto.id, dto.name, dto.items.map(item => PurchaseOrderItemMapper.toModel(item)), PurchaseOrderMapper.toModel(dto.purchaseOrder)) as T;
    }
    throw new Error('Invalid POItemsGroupedDTO');
  }
} 